import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Grid, InputAdornment, Autocomplete, Fab, Avatar, CircularProgress, Grow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { marked } from 'marked';

function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();

  // Chatbot State
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMaximized, setChatMaximized] = useState(false);
  const [messages, setMessages] = useState([{ sender: 'bot', text: 'Hello! I am here to help you about contracts. What can I do for you?' }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const sessionId = React.useMemo(() => 'session_' + Math.random().toString(36).substr(2, 9), []);
  const chatEndRef = useRef(null);

  useEffect(() => {
      if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
  }, [messages, chatOpen]);

  const handleSendMessage = async () => {
      if (!chatInput.trim() || chatLoading) return;
      const userMessage = chatInput;
      setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
      setChatInput('');
      setChatLoading(true);

      setMessages(prev => [...prev, { sender: 'bot', text: '' }]);

      try {
          const res = await fetch(`${API_URL}/chat_contracts/chat`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  ...headers
              },
              body: JSON.stringify({
                  session_id: sessionId,
                  user_input: userMessage
              })
          });

          if (!res.body) throw new Error('ReadableStream not yet supported in this browser.');
          const reader = res.body.getReader();
          const decoder = new TextDecoder('utf-8');

          while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              const lines = chunk.split('\n');
              let currentEvent = 'message';
              for (let line of lines) {
                  if (line.startsWith('event:')) {
                      currentEvent = line.substring(6).trim();
                  } else if (line.startsWith('data:')) {
                      let dataStr = line.substring(5).trim();
                      if (dataStr) {
                          try {
                              const parsed = JSON.parse(dataStr);
                              if (currentEvent === 'message') {
                                  let text = parsed.token || parsed.text || parsed.response || '';
                                  if (text) {
                                      setMessages(prev => {
                                          const base = [...prev];
                                          base[base.length - 1].text += text;
                                          return base;
                                      });
                                  }
                              } else if (currentEvent === 'error') {
                                  setMessages(prev => {
                                      const base = [...prev];
                                      base[base.length - 1].text = 'Sorry, there was an error processing your request.';
                                      return base;
                                  });
                              }
                          } catch (e) {
                              if (dataStr !== 'null' && dataStr !== '[DONE]') {
                                  setMessages(prev => {
                                      const base = [...prev];
                                      base[base.length - 1].text += dataStr;
                                      return base;
                                  });
                              }
                          }
                      }
                  }
              }
          }
      } catch (error) {
          console.error('Chat error:', error);
          setMessages(prev => {
              const base = [...prev];
              base[base.length - 1].text = 'Sorry, there was an error processing your request.';
              return base;
          });
      } finally {
          setChatLoading(false);
      }
  };

  // Form Data
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  
  const [newContract, setNewContract] = useState({
      project_id: '',
      client_id: '',
      country_id: '',
      division_id: '',
      description: '',
      ddp: '',
      file: null
  });

  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('user_role') || '';
  const isChatbotAllowed = userRole === 'Admin' || userRole === 'Contracts Manager';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conRes, projRes, cliRes, couRes, divRes] = await Promise.all([
          axios.get(`${API_URL}/contracts`, { headers }),
          axios.get(`${API_URL}/projects`, { headers }),
          axios.get(`${API_URL}/clients`, { headers }),
          axios.get(`${API_URL}/setup/countries`, { headers }),
          axios.get(`${API_URL}/setup/divisions`, { headers })
      ]);
      setContracts(conRes.data);
      setProjects(projRes.data);
      setClients(cliRes.data);
      setCountries(couRes.data);
      setDivisions(divRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
      setCreateLoading(true);
      try {
          const formData = new FormData();
          formData.append('project_id', newContract.project_id);
          formData.append('client_id', newContract.client_id);
          formData.append('country_id', newContract.country_id);
          formData.append('division_id', newContract.division_id);
          formData.append('description', newContract.description);
          formData.append('ddp', newContract.ddp);
          if (newContract.file) {
              formData.append('contract_pdf', newContract.file);
          }

          await axios.post(`${API_URL}/contracts`, formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
          setOpenAdd(false);
          fetchData();
          setNewContract({
            project_id: '', client_id: '', country_id: '', division_id: '', 
            description: '', ddp: '', file: null
          });
      } catch (err) {
          alert('Error creating contract');
          console.error(err);
      } finally {
          setCreateLoading(false);
      }
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Signed': return 'success';
          case 'Done by Client': return 'info';
          case 'In Progress': return 'warning';
          default: return 'default';
      }
  };

  const filteredContracts = contracts.filter(c => {
      if (filter !== 'All' && c.status !== filter) return false;
      if (search) {
          const lowerSearch = search.toLowerCase();
          return (
              (c.client_name && c.client_name.toLowerCase().includes(lowerSearch)) ||
              (c.project_name && c.project_name.toLowerCase().includes(lowerSearch)) ||
              (c.description && c.description.toLowerCase().includes(lowerSearch))
          );
      }
      return true;
  });

  if (loading) return <Loading />;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                    <DescriptionIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                    Contracts Management
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Manage and track all company contracts
                </Typography>
            </Box>
            
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setOpenAdd(true)}
                sx={{ 
                    position: 'relative',
                    zIndex: 2,
                    borderRadius: 2, 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 1,
                    bgcolor: 'white',
                    color: '#1e3c72',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                }}
            >
                New Contract
            </Button>
            
            {/* Decorative background circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        </Box>
      </Grow>

      {/* Filters and Controls */}
      <Paper elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {['All', 'New', 'In Progress', 'Done by Client', 'Signed'].map(f => (
                <Chip 
                    key={f} 
                    label={f} 
                    onClick={() => setFilter(f)} 
                    color={filter === f ? 'primary' : 'default'} 
                    variant={filter === f ? 'filled' : 'outlined'}
                    sx={{ 
                        fontWeight: 'bold', 
                        borderRadius: 2, 
                        px: 1, 
                        transition: 'all 0.2s',
                        bgcolor: filter === f ? '#2563eb' : 'transparent',
                        color: filter === f ? 'white' : '#64748b',
                        border: filter === f ? 'none' : '1px solid #cbd5e1',
                        '&:hover': { 
                            bgcolor: filter === f ? '#1d4ed8' : '#f8fafc',
                            transform: 'translateY(-1px)'
                        }
                    }}
                />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
            <TextField 
                placeholder="Search contracts..." 
                size="small" 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }}/></InputAdornment>,
                    sx: { borderRadius: 3, bgcolor: '#f8fafc', input: { py: 1.5, fontSize: '0.9rem', color: '#1e293b' } }
                }}
                sx={{ 
                    flexGrow: 1, 
                    minWidth: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1px' }
                    }
                }}
            />
          </Box>
        </Box>
      </Paper>

      <Grow in={true} timeout={1200}>
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
          <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
          <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }} aria-label="contracts table">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>ID & Project</TableCell>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Created By</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                  {filteredContracts.map(c => (
                      <TableRow 
                        key={c.id} 
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', transition: 'background-color 0.2s, transform 0.1s', '&:hover': { bgcolor: '#f8fbff !important' } }}
                        onClick={() => navigate(`/contracts/${c.id}`)}
                      >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Avatar sx={{ 
                                    bgcolor: '#eff6ff', 
                                    color: '#2563eb', 
                                    fontWeight: '900', 
                                    width: 46, height: 46, 
                                    boxShadow: '0 4px 10px rgba(37,99,235,0.2)'
                                }}>
                                    {c.id}
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: '800', color: '#3b82f6', bgcolor: '#eff6ff', px: 1, py: 0.25, borderRadius: 1.5, display: 'inline-block' }}>CT-{c.id}</Typography>
                                    {c.project_name && c.project_name !== 'N/A' && (
                                        <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.95rem', mt: 0.5 }}>{c.project_name}</Typography>
                                    )}
                                </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>{c.client_name}</TableCell>
                          <TableCell>
                              <Chip 
                                label={c.status} 
                                color={getStatusColor(c.status)} 
                                size="small" 
                                sx={{ height: 26, fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '6px', px: 1 }} 
                              />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300, color: '#64748b', fontWeight: 500, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.description}
                          </TableCell>
                          <TableCell>
                              <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>{c.country} - {c.division}</Typography>
                              <Typography variant="caption" color="textSecondary" fontWeight="600">{c.ddp}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>{c.created_by}</TableCell>
                      </TableRow>
                  ))}
                  {contracts.length === 0 && (
                      <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                              No contracts found.
                          </TableCell>
                      </TableRow>
                  )}
              </TableBody>
          </Table>
          </Box>
        </Paper>
        </Grow>

      {/* Add Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Add New Contract
            <IconButton size="small" onClick={() => setOpenAdd(false)} sx={{ color: 'white' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                
                {/* Basic Info */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={clients}
                                getOptionLabel={(option) => option.name || ""}
                                value={clients.find(c => c.id === newContract.client_id) || null}
                                onChange={(event, newValue) => {
                                    setNewContract({...newContract, client_id: newValue ? newValue.id : ''});
                                }}
                                renderInput={(params) => <TextField {...params} label="Client" fullWidth variant="outlined" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={projects}
                                getOptionLabel={(option) => option.name || ""}
                                value={projects.find(p => p.id === newContract.project_id) || null}
                                onChange={(event, newValue) => {
                                    setNewContract({...newContract, project_id: newValue ? newValue.id : ''});
                                }}
                                renderInput={(params) => <TextField {...params} label="Project (Optional)" fullWidth variant="outlined" />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                multiline 
                                rows={3} 
                                label="Description" 
                                value={newContract.description} 
                                onChange={e => setNewContract({...newContract, description: e.target.value})} 
                                variant="outlined"
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Classification & Details */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Classification & Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Country</InputLabel>
                                <Select 
                                    value={newContract.country_id} 
                                    label="Country" 
                                    onChange={e => setNewContract({...newContract, country_id: e.target.value})}
                                >
                                    {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>Division</InputLabel>
                                <Select 
                                    value={newContract.division_id} 
                                    label="Division" 
                                    onChange={e => setNewContract({...newContract, division_id: e.target.value})}
                                >
                                    {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel>DDP Location</InputLabel>
                                <Select 
                                    value={newContract.ddp} 
                                    label="DDP Location" 
                                    onChange={e => setNewContract({...newContract, ddp: e.target.value})}
                                >
                                    <MenuItem value="Lebanon">Lebanon</MenuItem>
                                    <MenuItem value="KSA">KSA</MenuItem>
                                    <MenuItem value="Qatar">Qatar</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth sx={{ height: '56px', borderRadius: '4px' }}>
                                Upload Contract PDF
                                <input 
                                    type="file" 
                                    hidden 
                                    accept="application/pdf" 
                                    onChange={e => setNewContract({...newContract, file: e.target.files[0]})} 
                                />
                            </Button>
                            {newContract.file && <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>{newContract.file.name}</Typography>}
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setOpenAdd(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
            <Button 
                onClick={handleCreate} 
                variant="contained" 
                disabled={createLoading}
                sx={{ 
                    borderRadius: 2, 
                    fontWeight: 'bold', 
                    px: 3, 
                    py: 1,
                    background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)',
                    boxShadow: '0 4px 12px rgba(30,60,114,0.3)'
                }}
            >
              {createLoading ? 'Creating...' : 'Create Contract'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Chatbot Floating UI */}
      {isChatbotAllowed && (
        <>
          {!chatOpen && (
            <Fab 
          color="primary" 
          aria-label="chat" 
          onClick={() => setChatOpen(true)}
          sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000, boxShadow: 6 }}       
        >
          <ChatIcon sx={{ fontSize: 32 }} />
        </Fab>
      )}

      {chatOpen && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: chatMaximized ? 0 : 32,
            right: chatMaximized ? 0 : 32,
            width: chatMaximized ? '100vw' : { xs: '90%', sm: 450 },
            height: chatMaximized ? '100vh' : '70vh',
            maxHeight: chatMaximized ? 'none' : 700,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            borderRadius: chatMaximized ? 0 : 4,
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{ 
            background: 'linear-gradient(to right, #1976d2, #4791db)', 
            color: 'white', 
            px: 3, 
            py: 2.5, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 ,color:'white'}}> Chat</Typography>
            </Box>
            <Box>
              <IconButton size="small" onClick={() => setChatMaximized(!chatMaximized)} sx={{ color: 'white', mr: 1 }}>
                {chatMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#f8f9fa' }}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                <Paper
                  elevation={msg.sender === 'user' ? 2 : 1}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: msg.sender === 'user' ? '#1976d2' : 'white', 
                    color: msg.sender === 'user' ? 'white' : 'text.primary',    
                    borderRadius: 3,
                    borderBottomRightRadius: msg.sender === 'user' ? 0 : 3,     
                    borderBottomLeftRadius: msg.sender === 'user' ? 3 : 0,      
                  }}
                >
                  <Typography 
                    variant="body1" 
                    component="div"
                    sx={{ 
                      lineHeight: 1.5,
                      '& p': { m: 0, mb: 1, '&:last-of-type': { mb: 0 } },
                      '& pre': { bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1, overflowX: 'auto', my: 1 },
                      '& code': { fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.1)', px: 0.5, borderRadius: 1 },
                      '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                      '& a': { color: 'inherit', textDecoration: 'underline' },
                      whiteSpace: msg.sender === 'user' ? 'pre-wrap' : 'normal'
                    }}
                    dangerouslySetInnerHTML={{ __html: msg.sender === 'user' ? (msg.text || '...') : marked.parse(msg.text || '...') }}
                  />  
                </Paper>
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5 }}>
            <TextField
              size="medium"
              fullWidth
              placeholder="Type your message here..."
              disabled={chatLoading}
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
              sx={{ 
                '& .MuiOutlinedInput-root': { borderRadius: 4 } 
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSendMessage} 
              disabled={chatLoading || !chatInput.trim()}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white', 
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                borderRadius: '50%',
                p: 1.5
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
          )}
        </>
      )}
    </Box>
  );
}

export default Contracts;
